import { Badge, Button, Col, Image, Pagination, Popover, Row, Tooltip, Typography } from "antd";
import { Table } from "antd/es";
import { ColumnsType } from "antd/lib/table";
import { SorterResult } from "antd/lib/table/interface";
import moment from 'moment';
import React, { Key, ReactNode, useEffect, useMemo, useState } from "react";
import Icon from "../../../../assets/icons/icon";
import _t from "../../../../lang/translate";
import format from "../../../../utilities/formatNumbers";
import { isValidVin, normalize } from "../../../../utilities/stringExtraFunctions";
import { QueryData } from "./marketPlace";
import { Advert, CatalogWarning, CatalogModeration, PaginatedAdverts } from "./types";
import MarketPlaceWarnings, { WarningIndicators } from "./marketPlaceWarnings";
import { getPermissions, getSavedEnums } from "../../../../services/authService";
import { Vehicle } from "../../../../types/appTypes";


type Props = {
  data: PaginatedAdverts;
  moderation: CatalogModeration;
  onWarningChanged: (warning: CatalogWarning) => void;
  preSelectedAdvertIds: number[]
  advertSelections: Advert[]
  setAdvertSelections: React.Dispatch<React.SetStateAction<Advert[]>>
  queryData: QueryData
  setQueryData: React.Dispatch<React.SetStateAction<QueryData>>
  vehicle: Vehicle | null;
}


export default function MarketPlaceTable({ data, preSelectedAdvertIds, advertSelections, setAdvertSelections, queryData, setQueryData, vehicle, moderation, onWarningChanged }: Props) {
  const canSeeWarnings = getPermissions().includes("ad_catalog.manage_ad_warnings");
  const tableColumns: ColumnsType<Advert> = canSeeWarnings ? [{
    key: "moderation",
    title: _t("catalog_warnings"),
    width: 96,
    render: (_value, advert) => <MarketPlaceWarnings advert={advert} moderation={moderation} onChanged={onWarningChanged} />,
  }, ...columns] : columns;
  const [selectedAdvert, setSelectedAdvert] = useState<Advert | null>(null) // Selected for detailed view
  const { pagination } = queryData
  const checkedAds = useMemo(() => new Set(advertSelections.map(ad => ad.id)), [advertSelections])

  const dataSource = useMemo(() => {
    const preSelectedAdIdSet = new Set(preSelectedAdvertIds)
    const validVin = isValidVin(vehicle?.vin);

    const base = (data?.data ?? [])
      .filter(ad => !preSelectedAdIdSet.has(ad.id)
        && !checkedAds.has(ad.id)
        && (!validVin || ad.vin !== vehicle?.vin)
      )

    const combined = [
      ...(advertSelections ?? []),
      ...base,
    ]
    return combined;
  }, [data, advertSelections, checkedAds])

  function checkAdvert(id: number) {
    setAdvertSelections((prev) => {
      const selectedSet = new Set(prev.map(ad => ad.id))
      if (selectedSet.has(id)) {
        selectedSet.delete(id)
        return prev.filter(ad => selectedSet.has(ad.id))
      } else {
        const newSelectedAd = data.data.find(ad => ad.id === id)
        return newSelectedAd ? [...prev, newSelectedAd] : prev
      }
    })
  }


  const handleRowClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, record: Advert | null) => {
    if ((e.ctrlKey || e.metaKey) && e.button === 0 && !!record) {
      checkAdvert(record.id)
    } else {
      if (!record) {
        setSelectedAdvert(null)
      } else {
        const ad = data?.data?.find(ad => ad.id === record.id)
        setSelectedAdvert(ad ?? null)
      }
    }
  }

  return (
    <>
      <Table columns={tableColumns} dataSource={dataSource} size="small"
        style={{
          height: selectedAdvert ? "calc(100% - 228px - 57px)" : "calc(100% - 57px)",
        }}
        pagination={false}
        onChange={(pagination, filters, sorter) => {
          setQueryData((prev) => ({
            ...prev,
            sorter: sorter as SorterResult<Advert>,
          }))
        }}

        className="tax-adverts-market-place-table"
        tableLayout="fixed"
        scroll={{ x: 1100, y: "100%" }}
        rowKey={"id"}
        rowSelection={{
          selectedRowKeys: [...checkedAds],
          onSelect(record, selected, selectedRows) {
            checkAdvert(record.id)
          },
          hideSelectAll: true,
          selections: false,
          fixed: true,
        }}
        onRow={(record) => {
          let className = ""
          if (selectedAdvert?.id && record.id === selectedAdvert?.id) {
            className += " show-details"
          }
          if (checkedAds.has(record.id)) {
            className += " selected"
          }
          if (!normalize(record.description)) {
            className += " text-danger"
          }

          return {
            className,
            onClick: (e) => handleRowClick(e, record),
          }
        }}
      />
      <Pagination
        onChange={(page, pageSize) => {
          setQueryData((prev) => ({
            ...prev,
            pagination: {
              perPage: pageSize ?? prev.pagination.perPage ?? data?.meta?.per_page ?? 50,
              page: page ?? prev.pagination.page ?? data?.meta?.current_page ?? 1
            },
          }))
        }}
        size="small"
        className="market-place-pagination"
        total={data.meta?.total}
        current={pagination.page}
        pageSize={pagination.perPage}
        pageSizeOptions={[50, 100, 200]}
        showTotal={(total, [from, to]) => (
          <>
            {from}-{to} af <strong>{total}</strong> {_t("total")}
          </>
        )}
        defaultPageSize={50}
        defaultCurrent={1}
      />
      {selectedAdvert && (
        <AdvertDetails onClose={() => setSelectedAdvert(null)} advert={selectedAdvert} moderation={canSeeWarnings ? moderation : {}} />
      )}
    </>

  )
}

function AdvertDetails({ advert, onClose, moderation }: { advert: Advert, onClose: () => void, moderation: CatalogModeration }) {
  const [visible, setVisible] = useState(false)
  return (
    <div className="shadow" style={{
      background: "#fdfeff", flex: "0 0 auto",
    }}>
      <Row wrap={false} className="border-light border-rounded" style={{ height: 228 }}>
        <Col span={13} className="p-1 d-flex gap-1">
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
            justifyContent: "space-between",
            height: "100%"
          }}>
            <Image height={160} width={160} src={advert.images?.[0]} style={{ flex: "0 0 auto", objectFit: "contain" }}
              onClick={() => setVisible(true)}
              preview={{ visible: false, }}
            />
            <Popover title={advert.seller?.name}
              content={
                <div>
                  <div>{advert.seller?.address}</div>
                  <div>{advert.seller?.zip} {advert.seller?.city}</div>
                  <div>Tlf: {advert.seller?.phone}</div>
                  <div>Mail: <a href={"mailto:" + advert.seller?.email}>{advert.seller?.email}</a></div>
                </div>
              }
            >
              <a style={{ maxWidth: 160 }} className="text-smallest text-ellipsis">{advert.seller?.name}</a>
            </Popover>
            <Image.PreviewGroup preview={{
              visible: visible,
              onVisibleChange: vis => setVisible(vis),
            }}>
              <div style={{ display: "none" }}>
                {advert.images?.map((image, index) => (
                  <Image key={index} src={image} />
                ))}
              </div>
            </Image.PreviewGroup>
          </div>
          <div style={{ flex: "1 1 0%" }}>
            <Row justify="space-between" className="mb-05">
              <Col>
                <h4 className="mb-0">{advert.manufacturer} {advert.model} <WarningIndicators advert={advert} moderation={moderation} /></h4>
                <h5 className="mb-">{format.price(advert.price?.amount)}</h5>
              </Col>
              <Col>
                <Button onClick={onClose} shape="circle" icon={<Icon name="close-outline" />} />
              </Col>
            </Row>

            <AdvertDetailItem label={_t("brand")} value={advert.manufacturer} />
            <AdvertDetailItem label={_t("model")} value={advert.model} />
            <AdvertDetailItem label={_t("variant")} value={advert.variant} />
            <AdvertDetailItem label={_t("firstRegDate")} value={format.date(advert.firstRegDate)} />
            <AdvertDetailItem label={_t("vin")} value={advert.vin} />
            <AdvertDetailItem label={_t("mileage")} value={format.milage(advert.mileage)} />
            <AdvertDetailItem label={_t("DAT", "eCode")} value={advert.code?.slice(0, 24)} />
          </div>
        </Col>
        <Col className="border-left-light p-1">
          <h4>{_t("description")}</h4>
          <Description description={advert.description} />
        </Col>
      </Row>
    </div >
  )
}
function AdvertDetailItem({ label, value }: { label: string; value: ReactNode; }) {
  return (
    <Row>
      <Col flex={"0 0 100px"} className="text-secondary">{label}</Col>
      <Col>{value}</Col>
    </Row>
  )
}

function Description({ description }: { description: string | null | undefined }) {
  const [showPopover, setShowPopover] = useState(false)
  const content = highlightText(description ?? "", ['moms', 'afgift', 'tax'])
  useEffect(() => {
    const overlay = document.querySelector('.ant-popover.description-overlay')
    const stopPropagation = (e: any) => e.stopPropagation()
    overlay?.addEventListener('click', stopPropagation)
    return () => {
      overlay?.removeEventListener('click', stopPropagation)
    }
  }, [showPopover])
  return (
    <Typography.Paragraph
      ellipsis={{
        rows: 8,
        expandable: true, // important
        symbol: (
          <Popover
            trigger="click"
            visible={showPopover}
            onVisibleChange={setShowPopover}
            overlayClassName="description-overlay"
            title={_t("description")}
            overlayInnerStyle={{
              maxWidth: 496,
              maxHeight: 768,
              overflow: "auto",
            }}
            content={
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
              >
                {content}
              </div>
            }
          >
            <span
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowPopover(true)
              }}
            >
              mere
            </span>
          </Popover>
        ),
      }}
    >
      {content}
    </Typography.Paragraph>
  )
}
function highlightText(text: string, terms: string[]) {
  if (!terms.length) return text
  const escaped = terms.map(escapeRegExp).join("|")
  const regex = new RegExp(`(${escaped})`, "gi")
  return text.split(regex).map((part, index) => {
    const isMatch = terms.some(term => term.toLowerCase() === part.toLowerCase())
    return isMatch ? (
      <mark className="text-danger text-semibold" key={index}>{part}</mark>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )
  })

}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

const savedEnums = getSavedEnums()
const fuelMap = new Map(savedEnums?.vehicle.fuel.map(fuel => [fuel.id, fuel]))

const columns: ColumnsType<Advert> = [
  {
    dataIndex: "type",
    key: "type",
    title: _t("type"),
    width: 72,
    render(value, record, index) {
      return (
        <>
          <span title={record.type ?? ""}>{record.type?.slice(0, 1)}</span>
          {' - '}
          <span>{fuelMap.get(record.fuel ?? -1)?.title}</span>
        </>
      )
    },
  },
  {
    dataIndex: "manufacturer",
    key: "manufacturer",
    title: _t("brand"),
    width: 96,
    ellipsis: true,
    sorter: true,
  },
  {
    dataIndex: "name",
    key: "name",
    title: _t("model"),
    ellipsis: true,
    render(value, record, index) {
      let title = "Annoncen har ingen stelnummer"
      let color: "error" | "warning" | "success" = "error"
      if (isValidVin(record.vin)) {
        color = "warning"
        title = record.vin
      }
      if (isValidVin(record.vin) && record.eCode) {
        color = "success"
        title += ", " + record.eCode
      }
      return (
        <div>
          <Tooltip title={title} >
            <Badge className="mr-03" status={color} />
          </Tooltip>
          {value}
        </div>
      )
    },
    sorter: true,
  },
  {
    dataIndex: "firstRegDate",
    key: "firstRegDate",
    ellipsis: true,
    title: _t("firstRegDate"),
    width: 100,
    sorter: true,
    render: (date) => format.date(date)
  },
  {
    dataIndex: "newPrice",
    key: "newPrice",
    title: _t("new_price"),
    width: 72,
    sorter: false,
    render(value, record, index) {
      return format.integer(record.newPrice?.amount)
    },
  },
  {
    dataIndex: "equipmentPrice",
    key: "equipmentPrice",
    title: _t("equipment_price"),
    width: 72,
    sorter: false,
    render(value, record, index) {
      const amount = format.integer(record.equipmentPrice?.amount);
      let title: string | null = "Usikker"
      let color: "error" | "warning" | "success" = "error"

      if (record.equipmentPriceIsReliable) {
        color = "success";
        title = null;
      }

      return (
        <div>
          <Tooltip title={title}>
            <Badge className="mr-03" status={color}/>
          </Tooltip>
          {amount}
        </div>
      )
    },
  },
  {
    dataIndex: "horsePower",
    key: "horsePower",
    width: 60,
    title: _t("horse_power").toUpperCase(),
    sorter: true,
  },
  {
    dataIndex: "mileage",
    key: "mileage",
    width: 72,
    title: _t("mileage_unit").toUpperCase(),
    sorter: true,
    render: (value) => format.integer(value),
  },
  {
    dataIndex: "price",
    key: "price",
    title: _t("price"),
    className: "text-bold",
    width: 72,
    sorter: true,
    render(value, record, index) {
      const priceFormatted = format.integer(!record.priceIncludesVat ? ((record.price?.amount ?? 0) * 1.25) : record.price?.amount)
      if (!record.priceIncludesVat) {
        return (
          <Tooltip placement="left" title={"ex moms " + format.number(record.price)}>
            <span style={{ textDecoration: "underline dotted" }}>{priceFormatted}</span>
          </Tooltip>
        )
      }
      return priceFormatted
    },
  },
  {
    dataIndex: "validFrom",
    key: "validFrom",
    title: "L-dage",
    width: 80,
    sorter: true,
    render: (value, record) => {
      const days = moment().diff(moment(record.validFrom), 'days')
      return <span className={days > 100 ? "text-danger text-bold" : ""}>{days}</span>
    }
  }
]
